import { ILeaf, INode } from 'shared/types/tree'
import { LeafTypes, NodeBodyKeys, NodeType } from 'shared/types/ingestion'
import { breadthFirstNodeCheck } from 'shared/utils/tree'

import { childListsOfType } from '../tree/masters/questions/utility'
import { objectFieldSort } from 'shared/utils'
import { srcsFromString } from '../ingestion/Utility'
import { useCallback } from 'react'
import { useGetGroupForProduct, useGetProductForId } from 'shared/api/tree'
import {
  allProducts,
  allProductsByPrimaryTreeId,
  useMastersByIdWithErrorNotification,
} from 'shared/api/masters'
import { useAtomValue } from 'infrastructure/state/jotai.wrapper'
import { retrieveIds, updateTreeNode } from '../tree/TreeRequests'
import { includesTest } from 'shared/utils/contentTransforms'
import { allContentForFilterTest } from '../tree/treeManipulation/utility'
import { useGetTreeById } from 'shared/hooks/trees/hooks'

export function nodeForId(nodeId: string, tree: INode) {
  let foundNode: INode | undefined
  for (const node of traverseTree(tree)) {
    if (node.id === nodeId) {
      foundNode = node
      break
    }
  }
  return foundNode
}

export function useNodeForIds({
  treeId,
  versionId,
  nodeId,
}: {
  treeId: string
  versionId: string
  nodeId: string
}) {
  const { tree } = useGetTreeById({ treeId, versionId })
  if (!tree) return
  return nodeForId(nodeId, tree)
}

export function leavesOfType(node: INode, leafType: LeafTypes) {
  return node.leaves?.filter((l) => l.type === leafType) || []
}

export function allLeavesOfType(node: INode, ...leafTypes: LeafTypes[]) {
  const allLeaves: ILeaf[] = []
  const nodeCheck = (node: INode) =>
    allLeaves.push(
      ...(node.leaves?.filter((leaf) =>
        leafTypes.includes(leaf.type as unknown as LeafTypes),
      ) || []),
    )
  breadthFirstNodeCheck(node, nodeCheck)
  return allLeaves
}

export function leafForId(tree: INode, leafId: string) {
  const toCheck = [tree]
  let leaf: ILeaf | undefined
  do {
    const currentNode = toCheck.pop()!
    toCheck.push(...(currentNode.nodes ?? []))
    leaf = currentNode.leaves?.find(({ id }) => id === leafId)
  } while (toCheck.length && !leaf)
  return leaf
}

export function leafOfType(node: INode, leafType: LeafTypes) {
  return leavesOfType(node, leafType)[0]
}

export function nodeAttributionLeaf(node: INode): ILeaf | undefined {
  return leafOfType(node, LeafTypes.Attribution)
}
export function nodeAttributionLeaves(node: INode) {
  return leavesOfType(node, LeafTypes.Attribution)
}
export function nodeAttribution(node: INode): string | undefined {
  return nodeAttributionLeaf(node)?.body?.value as string
}
export function nodeSubjectLeaf(node: INode): ILeaf | undefined {
  return leafOfType(node, LeafTypes.Subject)
}

export function nodeGradeLeaf(tree: INode): ILeaf | undefined {
  return leafOfType(tree, LeafTypes.Grade)
}

export function childNodesOfType(
  { nodes }: { nodes: INode[] },
  ...types: string[]
) {
  const filter = (n: INode) => types.includes(n.type)
  return nodes.filter(filter)
}

export function allNodesOfType(tree: INode, ...types: string[]): INode[] {
  const nodes: INode[] = []
  const nodeCheck = (n: INode) => types.includes(n.type) && nodes.push(n)
  breadthFirstNodeCheck(tree, nodeCheck)
  return nodes
}

export function useLatestProductVersion() {
  const masters = useMastersByIdWithErrorNotification()
  const getProduct = useGetProductForId()

  return useCallback(
    (productId: number) => {
      const product = getProduct(productId)
      if (!product?.primary_tree_tree_id) return
      return masters?.[product.primary_tree_tree_id]?.[0]
    },
    [masters, getProduct],
  )
}

export function lastEditedDate(tree: INode) {
  const nodesToCheck = [tree]
  let lastEdited: string = tree.updated_at || ''
  const checkThis = ({ updated_at }: { updated_at?: string }) => {
    if (!updated_at) return
    if (updated_at.localeCompare(lastEdited) > 0) {
      lastEdited = updated_at
    }
  }
  while (nodesToCheck.length) {
    const current = nodesToCheck.pop()!
    current.nodes && nodesToCheck.push(...current.nodes)
    checkThis(current)
    current.leaves?.forEach(checkThis)
  }
  return lastEdited
}

export function* traverseTree(...nodes: INode[]): IterableIterator<INode> {
  for (const node of nodes) {
    yield node
    yield* traverseTree(...(node.nodes ?? []))
  }
}

export function nodesOfTypeWithData(props: {
  tree: INode
  types?: string[]
  extraCheck?: (n: INode) => boolean
  searches?: (RegExp | string)[]
}) {
  const nodeString = (n: INode) => JSON.stringify({ ...n, nodes: [] })
  return nodesOfTypeWithSearch({ ...props, nodeString })
}

export function nodesOfTypeWithContentV2(props: {
  tree: INode
  types?: string[]
  extraCheck?: (n: INode) => boolean
  searches?: (RegExp | string)[]
}) {
  return nodesOfTypeWithSearch({
    ...props,
    nodeString: allContentForFilterTest,
  })
}

function nodesOfTypeWithSearch({
  tree,
  types = [],
  extraCheck = () => true,
  searches = [],
  nodeString,
}: {
  tree: INode
  types?: string[]
  extraCheck?: (n: INode) => boolean
  searches?: (RegExp | string)[]
  nodeString: (n: INode) => string
}) {
  const results: INode[] = []
  if (!types.length && !searches.length) return results
  const contentCheck = (toCheck: string) => {
    if (!searches.length) return true
    return !!searches.find((r) => {
      return includesTest(r, toCheck)
    })
  }
  const checkThis = (node: INode) => {
    if (node.type.startsWith('reference')) return
    if (!types.length || types.includes(node.type)) {
      const asString = nodeString(node)
      const hasContent = contentCheck(asString)
      if (hasContent && extraCheck(node)) {
        results.push(node)
      }
    }
  }
  breadthFirstNodeCheck(tree, checkThis)
  return results
}

export function nodesOfTypeWithContent({
  tree,
  types = [],
  content = [],
  extraCheck = () => true,
  caseSensitive = true,
}: {
  tree: INode
  types?: string[]
  content?: string[]
  extraCheck?: (n: INode) => boolean
  caseSensitive?: boolean
}) {
  const searches = content.map((c) => {
    const flags = caseSensitive ? '' : 'i'
    return new RegExp(c, flags)
  })
  return nodesOfTypeWithContentV2({
    tree,
    types,
    extraCheck,
    searches,
  })
}

export function nodesAndLeavesWithContent(tree: INode, content: string) {
  const nodes = nodesOfTypeWithContent({
    tree,
    content: [content],
  })
  return nodes.reduce<{ nodes: INode[]; leaves: ILeaf[] }>(
    ({ nodes, leaves }, node) => {
      if (!node.leaves?.length) {
        nodes.push(node)
        return { nodes, leaves }
      }
      if (JSON.stringify({ ...node, leaves: '' }).includes(content)) {
        nodes.push(node)
      }
      node.leaves.forEach((leaf) => {
        if (JSON.stringify(leaf).includes(content)) {
          leaves.push(leaf)
        }
      })
      return { nodes, leaves }
    },
    { nodes: [], leaves: [] },
  )
}

export function getAttachmentLeavesByName(tree: INode, name: string = '') {
  return allLeavesOfType(tree, LeafTypes.Attachment).filter((leaf) =>
    (leaf?.body?.file?.name?.toLowerCase() ?? '').includes(name.toLowerCase()),
  )
}

export interface IProductData {
  treeID: string
  title: string
  productID: number
}

export interface IMasterGroups {
  [title: string]: IProductData[]
}

export function useGroupedMasters() {
  const getGroup = useGetGroupForProduct()
  const products = useAtomValue(allProducts)
  const productsById = useAtomValue(allProductsByPrimaryTreeId)

  return useCallback(
    (excluding: string[]) => {
      const { grouped, notGrouped } = products.reduce<{
        grouped: IMasterGroups
        notGrouped: IProductData[]
      }>(
        (acc, product) => {
          const { primary_tree_tree_id, id, title } = product
          if (!primary_tree_tree_id) return acc
          if (excluding.includes(primary_tree_tree_id)) return acc
          const group = getGroup(product)
          if (!group) {
            acc.notGrouped.push({
              treeID: primary_tree_tree_id,
              productID: id,
              title,
            })
            return acc
          }
          const { name } = group
          if (!(name in acc.grouped)) {
            acc.grouped[name] = []
          }
          acc.grouped[name].push({
            treeID: primary_tree_tree_id,
            productID: id,
            title,
          })
          return acc
        },
        { grouped: {}, notGrouped: [] },
      )

      notGrouped.sort(objectFieldSort('title'))
      Object.values(grouped).forEach((group) =>
        group.sort(objectFieldSort('title')),
      )

      return {
        grouped,
        notGrouped,
      }
    },
    [getGroup, products, productsById],
  )
}

export function getQuestionType(q: INode) {
  return leafOfType(q, LeafTypes.Question)?.body.value
}

export function getQuestionTeacherNoteNode(q: INode) {
  const [nodeList] = childListsOfType(q, NodeType.Inline, NodeType.Note)
  return nodeList?.nodes[0]
}

export function getQuestionTeachersNoteContent(q: INode) {
  return getQuestionTeacherNoteNode(q)?.body?.value
}

export function isStudentFacing(n: INode) {
  return n.body?.[NodeBodyKeys.StudentFacing]
}

export async function makeStudentFacing(n: INode) {
  const body = {
    ...n.body,
    [NodeBodyKeys.StudentFacing]: true,
  }
  await updateTreeNode({ ...retrieveIds(n), body })
}

const removeKeys = (
  object: { [key: string]: any } | undefined,
  keys: string[],
) => {
  if (!object) return
  keys.forEach((key) => {
    if (key.includes('.')) {
      const [parent, childKey] = key.split('.')
      removeKeys(object[parent], [childKey])
      return
    }
    delete object[key]
  })
}

export function stripTreeJsonForAppend(tree: INode) {
  const keysToRemove = [
    'tree_id',
    'version',
    'id',
    'parent_id',
    'created_at',
    'updated_at',
    'user',
    'depth',
  ]
  const removeDefaultKeys = (object: { [key: string]: any }) =>
    removeKeys(object, keysToRemove)
  const nodeUpdateFunc = (node: INode) => {
    removeKeys(node, keysToRemove)
    node.leaves?.forEach(removeDefaultKeys)
  }
  breadthFirstNodeCheck(tree, nodeUpdateFunc)
  return tree
}

export function allUrlsInTree(tree: INode) {
  const urls = new Set(srcsFromString(JSON.stringify(tree)))
  allLeavesOfType(tree, LeafTypes.Attachment).forEach((leaf) => {
    const url = leaf.body.file.url
    if (url) urls.add(url)
  })
  return Array.from(urls)
}

export function isReferenceNode(node: INode) {
  return node.type.startsWith('reference:')
}
